<div class="mx-4 md:mx-10 xl:mx-20 2xl:max-w-[1600px] 2xl:mx-auto pb-16">
  <div *ngIf="loading" class="flex items-center justify-center py-24">
    <p class="text-secondary_Light_Text_Color text-sm font-medium">
      Loading report&hellip;
    </p>
  </div>

  <div *ngIf="loadFailed" class="py-24 text-center">
    <p class="text-primary_Text_Color text-lg font-medium">
      {{ reportNotFound ? "Report not found" : "This report could not be loaded" }}
    </p>
    <p class="text-secondary_Light_Text_Color text-sm mt-2">
      {{
        reportNotFound
          ? "This link is invalid or the report no longer exists. Ask whoever shared it for a new one."
          : "The link may have expired. Ask whoever shared it for a new one."
      }}
    </p>
    <a
      [routerLink]="_commonServices.Navigations.HR_USER_DASHBOARD"
      class="mt-6 inline-flex items-center px-4 py-2 rounded-lg bg-primary_Bg_Button_Color text-primary_Bg_Btn_Text_color text-sm font-semibold hover:bg-hover_primary_Bg_Button_Color transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue_Color"
    >
      Go to Dashboard
    </a>
  </div>

  <ng-container *ngIf="!loading && !loadFailed">

  <div class="pt-5">
    <nav
      class="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm leading-5 text-slate_500 poppinsMedium"
      aria-label="Breadcrumb"
    >
      <a
        [routerLink]="_commonServices.Navigations.HR_USER_DASHBOARD"
        class="hover:text-dark_navy hover:underline"
      >
        Dashboard
      </a>
      <span aria-hidden="true" class="text-slate_300">/</span>
      <span class="truncate max-w-[20rem]">{{
        reportContext || reportLabel
      }}</span>
      <span aria-hidden="true" class="text-slate_300">/</span>
      <span class="truncate max-w-[20rem] text-slate_700">{{
        candidateName
      }}</span>
    </nav>
    <div class="flex flex-wrap items-center justify-between gap-3 mt-2">
      <h1 class="text-2xl leading-[120%] text-dark_navy poppinsSemiBold">
        Candidate Details
      </h1>
      <a
        [routerLink]="_commonServices.Navigations.HR_USER_DASHBOARD"
        class="inline-flex items-center px-4 py-2 rounded-lg bg-primary_Bg_Button_Color text-primary_Bg_Btn_Text_color text-sm font-semibold hover:bg-hover_primary_Bg_Button_Color transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue_Color"
      >
        Go to Dashboard
      </a>
    </div>
  </div>

  <div class="flex flex-col gap-5 mt-5">
    <!-- Candidate card: same frame as the HR view-candidate page -->
    <div
      class="flex flex-wrap items-center justify-between gap-4 bg-white border border-slate_200 shadow-card-sm rounded-[10px] py-5 px-6"
    >
      <div class="flex items-center gap-5 min-w-0">
        <div
          class="w-[72px] h-[72px] shrink-0 rounded-full bg-slate_200 flex items-center justify-center text-slate_500 poppinsSemiBold text-[26px]"
          aria-hidden="true"
        >
          {{ getInitials(candidateName) }}
        </div>
        <div class="min-w-0">
          <p
            class="text-[28px] leading-[120%] text-dark_navy poppinsSemiBold truncate"
            [title]="candidateName"
          >
            {{ candidateName || "-" }}
          </p>
          <div class="flex flex-wrap items-center gap-2 mt-1">
            <span
              *ngIf="reportLabel"
              class="rounded-md px-2 py-0.5 bg-blue_50 text-blue_Color text-xs poppinsSemiBold"
            >
              {{ reportLabel }}
            </span>
            <span
              *ngIf="reportContext"
              class="text-sm leading-5 text-slate_500 poppinsMedium truncate"
            >
              {{ reportContext }}
            </span>
          </div>
        </div>
      </div>

      <!-- Final summary: the overall weighted score is the headline -->
      <div *ngIf="reportKind === 'sr' && finalSummary" class="text-right">
        <p class="text-sm leading-5 text-slate_500 poppinsMedium">Final score</p>
        <p class="text-[32px] leading-[120%] text-dark_blue poppinsBold">
          {{ fmt(finalSummary?.finalScore) }}%
        </p>
      </div>

      <!-- Bot stage: total across interview topics -->
      <div *ngIf="reportKind === 'bot' && botTotal as total" class="text-right">
        <p class="text-sm leading-5 text-slate_500 poppinsMedium">Interview score</p>
        <p class="text-[32px] leading-[120%] text-dark_blue poppinsBold">
          {{ fmt(total.score) }}<span class="text-lg text-slate_400 poppinsSemiBold">
            / {{ total.max }}</span
          >
        </p>
      </div>

      <ng-container *ngIf="cultureFit as fff">
        <div
          *ngIf="fff?.candidateRank > 1"
          class="flex items-center gap-2 rounded-md px-4 py-2 bg-green_100 text-green_600 text-sm poppinsMedium"
        >
          <img [src]="_commonServices.Images.STATUS_CHECK_CIRCLE" alt="" class="w-4 h-4" />
          Culture fit
        </div>
        <div
          *ngIf="fff?.candidateRank <= 1"
          class="flex items-center gap-2 rounded-md px-4 py-2 bg-red_50 text-red_600 text-sm poppinsMedium"
        >
          <img [src]="_commonServices.Images.STATUS_WARNING_TRIANGLE" alt="" class="w-4 h-4" />
          Not a culture fit
        </div>
      </ng-container>
    </div>

    <ng-container *ngIf="reportKind === 'assessment'">
    <div class="grid grid-cols-1 xl:grid-cols-2 gap-5">
      <!-- Candidate Information -->
      <div class="bg-white border border-slate_200 rounded-[10px] shadow-card-section overflow-hidden">
        <div class="flex items-center gap-2 py-3.5 px-4 border-b border-slate_200">
          <span class="w-1 h-6 rounded-[1px_2px_2px_1px] bg-health-yellow-gradient"></span>
          <h2 class="text-base leading-[140%] text-slate_700 poppinsSemiBold">
            Candidate Information
          </h2>
        </div>
        <div class="info-row" [title]="driveAssessmentData?.userDto?.candidateId">
          <span class="info-row__icon bg-blue_50">
            <img [src]="_commonServices.Images.CANDIDATE_INFO_ID_CARD" alt="" />
          </span>
          <span class="info-row__label text-dark_blue poppinsSemiBold">Candidate ID</span>
          <span class="info-row__value text-my_profile_secondary_text poppinsMedium">
            {{ driveAssessmentData?.userDto?.candidateId || "-" }}
          </span>
        </div>
        <div class="info-row" [title]="driveAssessmentData?.driveName">
          <span class="info-row__icon bg-blue_50">
            <img [src]="_commonServices.Images.CANDIDATE_INFO_ID_CARD" alt="" />
          </span>
          <span class="info-row__label text-dark_blue poppinsSemiBold">Drive ID</span>
          <span class="info-row__value text-my_profile_secondary_text poppinsMedium">
            {{ driveAssessmentData?.driveId || "-" }}
          </span>
        </div>
        <div class="info-row">
          <span class="info-row__icon bg-blue_50">
            <img [src]="_commonServices.Images.CANDIDATE_INFO_CLOCK" alt="" />
          </span>
          <span class="info-row__label text-dark_blue poppinsSemiBold">Time taken</span>
          <span class="info-row__value text-my_profile_secondary_text poppinsMedium">
            {{
              timeTakenMinutes === null
                ? "-"
                : timeTakenMinutes + (timeTakenMinutes === 1 ? " Min" : " Mins")
            }}
          </span>
        </div>
      </div>

      <!-- Assessment Details -->
      <div class="bg-white border border-slate_200 rounded-[10px] shadow-card-section overflow-hidden">
        <div class="flex items-center gap-2 py-3.5 px-4 border-b border-slate_200">
          <span class="w-1 h-6 rounded-[1px_2px_2px_1px] bg-health-yellow-gradient"></span>
          <h2 class="text-base leading-[140%] text-slate_700 poppinsSemiBold">
            Assessment Details
          </h2>
        </div>
        <div class="info-row">
          <span class="info-row__icon bg-violet_100">
            <img [src]="_commonServices.Images.CANDIDATE_INFO_CLOCK" alt="" />
          </span>
          <span class="info-row__label text-dark_blue poppinsSemiBold">Allowed duration</span>
          <span class="info-row__value text-my_profile_secondary_text poppinsMedium">
            {{ driveAssessmentData?.userDto?.totalTestAllowedDuration ?? "-" }} Mins
          </span>
        </div>
        <div class="info-row">
          <span class="info-row__icon bg-violet_100">
            <img [src]="_commonServices.Images.SCORE_EVALUATION_SECTION" alt="" />
          </span>
          <span class="info-row__label text-dark_blue poppinsSemiBold">Questions</span>
          <span class="info-row__value text-my_profile_secondary_text poppinsMedium">
            {{ driveAssessmentData?.userDto?.totalQuestions ?? "-" }}
          </span>
        </div>
        <div class="info-row">
          <span class="info-row__icon bg-violet_100">
            <img [src]="_commonServices.Images.CANDIDATE_INFO_CLOCK" alt="" />
          </span>
          <span class="info-row__label text-dark_blue poppinsSemiBold">Started</span>
          <span class="info-row__value text-my_profile_secondary_text poppinsMedium">
            {{
              isValidDate(driveAssessmentData?.userDto?.startTime)
                ? (driveAssessmentData?.userDto?.startTime | date : "h:mm a, d MMM y")
                : "-"
            }}
          </span>
        </div>
        <div class="info-row">
          <span class="info-row__icon bg-violet_100">
            <img [src]="_commonServices.Images.CANDIDATE_INFO_CLOCK" alt="" />
          </span>
          <span class="info-row__label text-dark_blue poppinsSemiBold">Finished</span>
          <span class="info-row__value text-my_profile_secondary_text poppinsMedium">
            {{
              isValidDate(driveAssessmentData?.userDto?.endTime)
                ? (driveAssessmentData?.userDto?.endTime | date : "h:mm a, d MMM y")
                : "-"
            }}
          </span>
        </div>
      </div>
    </div>

    <!-- Section Scores -->
    <div class="bg-white border border-slate_200 rounded-[10px] shadow-card-section overflow-hidden">
      <div class="flex items-center gap-2 py-3.5 px-4 border-b border-slate_200">
        <span class="w-1 h-6 rounded-[1px_2px_2px_1px] bg-health-yellow-gradient"></span>
        <h2 class="text-base leading-[140%] text-slate_700 poppinsSemiBold">
          Section Scores
        </h2>
      </div>
      <div class="overflow-x-auto">
        <table class="report-table w-full border-collapse min-w-[56rem]">
          <thead class="bg-light_blue uppercase poppinsSemiBold text-sm text-dark_blue tracking-[0.025em]">
            <tr>
              <th scope="col" class="p-4 text-left border-b border-slate_300 whitespace-nowrap w-16">S.No</th>
              <th scope="col" class="p-4 text-left border-b border-slate_300 whitespace-nowrap">Section</th>
              <th scope="col" class="p-4 text-center border-b border-slate_300 whitespace-nowrap">Score</th>
              <th scope="col" class="p-4 text-center border-b border-slate_300 whitespace-nowrap">Questions</th>
              <th scope="col" class="p-4 text-center border-b border-slate_300 whitespace-nowrap">Correct</th>
              <th scope="col" class="p-4 text-center border-b border-slate_300 whitespace-nowrap">Incorrect</th>
              <th scope="col" class="p-4 text-center border-b border-slate_300 whitespace-nowrap">Unattempted</th>
              <th scope="col" class="p-4 text-left border-b border-slate_300 whitespace-nowrap">Status</th>
            </tr>
          </thead>
          <tbody>
            <tr
              *ngFor="let item of driveAssessmentData?.userDto?.sectionScoreDtoList; index as i"
              class="border-b border-grey_150 last:border-b-0"
            >
              <td class="py-[18px] px-4 text-[15px] poppinsMedium text-slate_500">{{ i + 1 }}</td>
              <td
                class="py-[18px] px-4 text-[15px] leading-[140%] poppinsMedium text-dark_navy max-w-[18rem] truncate"
                [title]="item?.sectionName"
              >
                {{ item?.sectionName || "-" }}
              </td>
              <td class="py-[18px] px-4 text-[15px] text-center poppinsSemiBold text-dark_navy">
                <ng-container
                  *ngIf="
                    !item?.onlyVideoQuestionSection &&
                      !item?.onlyTextTypeQuestionSection &&
                      item?.totalCandidateSectionScore != null;
                    else emptyCell
                  "
                >
                  {{ item?.totalCandidateSectionScore }}
                </ng-container>
              </td>
              <td class="py-[18px] px-4 text-[15px] text-center poppinsMedium text-my_profile_secondary_text">
                {{ item?.totalNoOfQuestions ?? "-" }}
              </td>
              <td class="py-[18px] px-4 text-[15px] text-center poppinsMedium text-my_profile_secondary_text">
                <ng-container
                  *ngIf="
                    !item?.onlyVideoQuestionSection &&
                      !item?.onlyTextTypeQuestionSection &&
                      item?.correctQuestions != null;
                    else emptyCell
                  "
                >
                  {{ item?.correctQuestions }}
                </ng-container>
              </td>
              <td class="py-[18px] px-4 text-[15px] text-center poppinsMedium text-my_profile_secondary_text">
                <ng-container *ngIf="item?.incorrectQuestions != null; else emptyCell">
                  {{ item?.incorrectQuestions }}
                </ng-container>
              </td>
              <td class="py-[18px] px-4 text-[15px] text-center poppinsMedium text-my_profile_secondary_text">
                <ng-container *ngIf="item?.unattemptedQuestions != null; else emptyCell">
                  {{ item?.unattemptedQuestions }}
                </ng-container>
              </td>
              <td class="py-[18px] px-4" [title]="item?.sectionStatus | replaceUnderscore">
                <span
                  *ngIf="item?.sectionStatus; else emptyCell"
                  class="status-pill"
                  [ngClass]="sectionStatusTone(item?.sectionStatus)"
                >
                  {{ item?.sectionStatus | replaceUnderscore }}
                </span>
              </td>
            </tr>
            <tr *ngIf="!driveAssessmentData?.userDto?.sectionScoreDtoList?.length">
              <td colspan="8" class="py-8 px-4 text-center text-sm text-slate_400 poppinsMedium">
                The candidate has not started this assessment yet.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    </ng-container>

    <ng-container *ngIf="reportKind === 'bot'">
      <ng-container *ngTemplateOutlet="botStage"></ng-container>
    </ng-container>

    <ng-container *ngIf="reportKind === 'sr'">
      <ng-container *ngTemplateOutlet="srReport"></ng-container>
    </ng-container>
  </div>

  <ng-container *ngIf="reportKind === 'assessment'">

  <!-- Soft Skills: one block per scored video answer (candidateAudioScoreDto is a list).
       No overflow clipping on this card: the rubric tooltip is absolutely positioned. -->
  <div
    *ngIf="audioScores.length"
    class="bg-white border border-slate_200 rounded-[10px] shadow-card-section mt-5"
  >
    <div class="flex items-center gap-2 py-3.5 px-4 border-b border-slate_200">
      <span class="w-1 h-6 rounded-[1px_2px_2px_1px] bg-health-yellow-gradient"></span>
      <h2 class="text-base leading-[140%] text-slate_700 poppinsSemiBold">
        Soft Skills
      </h2>
    </div>

    <div
      *ngFor="let entry of audioScores; index as e; last as isLast"
      [class.border-b]="!isLast"
      class="border-slate_200"
    >
      <p
        *ngIf="entry?.fffScore?.hrQuestion || entry?.questionText"
        class="py-3 px-4 bg-slate_50 border-b border-slate_200 text-sm leading-[21px]"
      >
        <span class="text-dark_blue poppinsSemiBold">Question: </span>
        <span class="text-my_profile_secondary_text poppinsMedium">{{
          entry?.fffScore?.hrQuestion || entry?.questionText
        }}</span>
      </p>
      <p
        *ngIf="entry?.error"
        class="py-6 px-4 text-center text-sm text-slate_500 poppinsMedium"
      >
        {{ entry.error }}
      </p>
      <table *ngIf="entry?.skills?.length" class="report-table w-full border-collapse">
        <thead class="bg-light_blue uppercase poppinsSemiBold text-sm text-dark_blue tracking-[0.025em]">
          <tr>
            <th scope="col" class="p-4 text-left border-b border-slate_300 whitespace-nowrap">Skill</th>
            <th scope="col" class="p-4 text-center border-b border-slate_300 whitespace-nowrap">Weight</th>
            <th scope="col" class="p-4 text-center border-b border-slate_300 whitespace-nowrap">Score</th>
            <th scope="col" class="p-4 text-left border-b border-slate_300 whitespace-nowrap">Rubric</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let item of entry.skills; let i = index" class="border-b border-grey_150">
            <td class="py-[18px] px-4 text-[15px] poppinsMedium text-dark_navy">
              {{ item?.skillName || "-" }}
            </td>
            <td class="py-[18px] px-4 text-[15px] text-center poppinsMedium text-my_profile_secondary_text">
              {{ item?.weight || "-" }}
            </td>
            <td class="py-[18px] px-4 text-[15px] text-center poppinsSemiBold text-dark_navy">
              {{ (item?.totalScore | number : "1.1-2") || "-" }}
            </td>
            <td class="py-[18px] px-4 relative">
              <button
                *ngIf="item?.rubricScore?.length; else emptyCell"
                type="button"
                class="text-[15px] poppinsMedium text-blue_Color hover:underline"
                (mouseenter)="showRubricTooltip($event, e + '-' + i)"
                (mouseleave)="hideRubricTooltip()"
                (focus)="showRubricTooltip($event, e + '-' + i)"
                (blur)="hideRubricTooltip()"
              >
                View rubric
              </button>
              <div
                *ngIf="activeTooltipIndex === e + '-' + i"
                [style.top.px]="tooltipPosition.top"
                [style.left.px]="tooltipPosition.left"
                class="absolute z-[99999] bg-white p-4 rounded shadow-lg border border-gray-200 w-auto"
              >
                <div *ngIf="item.rubricScore && item.rubricScore.length">
                  <h4 class="font-bold text-sm mb-2">Rubric Details:</h4>
                  <div *ngFor="let rubric of item.rubricScore" class="mb-3">
                    <div class="flex justify-between">
                      <span class="font-semibold text-sm">
                        Name: {{ rubric.name }}</span
                      >
                    </div>
                    <div>
                      <span class="text-sm">Score: {{ rubric.score }}</span>
                    </div>
                    <p
                      *ngIf="rubric.explanation"
                      class="text-xs text-gray-600 mt-1"
                    >
                      <ng-container
                        *ngFor="
                          let line of rubric.explanation.split('\\n');
                          let last = last
                        "
                      >
                        {{ line }}<br *ngIf="!last" />
                      </ng-container>
                    </p>
                  </div>
                </div>
                <div
                  *ngIf="!item.rubricScore || !item.rubricScore.length"
                  class="text-sm"
                >
                  No rubric data available
                </div>
              </div>
            </td>
          </tr>
          <tr class="bg-slate_50">
            <td class="py-[18px] px-4 text-[15px] poppinsBold text-dark_blue">Overall</td>
            <td></td>
            <td class="py-[18px] px-4 text-[15px] text-center poppinsBold text-dark_blue">
              {{ (entry?.softSkillTotalScore | number : "1.1-2") || "-" }}
            </td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <!-- Skills radar. The container class is looked up by createRadarChart(). -->
  <div
    *ngIf="softSkillData"
    class="bg-white border border-slate_200 rounded-[10px] shadow-card-section overflow-hidden mt-5"
  >
    <div class="flex items-center gap-2 py-3.5 px-4 border-b border-slate_200">
      <span class="w-1 h-6 rounded-[1px_2px_2px_1px] bg-health-yellow-gradient"></span>
      <h2 class="text-base leading-[140%] text-slate_700 poppinsSemiBold">
        Skills Evaluation
      </h2>
    </div>
    <div class="radar-chart-container flex items-center justify-center overflow-x-auto"></div>
    <p
      *ngIf="softSkillData?.error"
      class="py-8 px-4 text-center text-sm text-slate_500 poppinsMedium"
    >
      {{ softSkillData.error }}
    </p>
  </div>

  </ng-container>

  <!-- Feeds -->
  <div
    *ngIf="urlsResponseLength > 0"
    class="bg-white border border-slate_200 rounded-[10px] shadow-card-section overflow-hidden mt-5"
  >
    <!-- <div class="flex gap-4 mt-12 mb-5">
      <p class="text-primary_Text_Color text-xl font-medium my-auto">Feeds</p>
      <button
        (click)="startMerging()"
        class="p-3 bg-primary_Bg_Button_Color rounded-lg text-primary_Text_Color text-sm items-center truncate shadow-md ml-4"
      >
        Start Merging
      </button>
    </div> -->
    <div class="flex items-center gap-2 py-3.5 px-4 border-b border-slate_200">
      <span class="w-1 h-6 rounded-[1px_2px_2px_1px] bg-health-yellow-gradient"></span>
      <h2 class="text-base leading-[140%] text-slate_700 poppinsSemiBold">Feeds</h2>
    </div>

    <p
      *ngIf="visibleFeedCount === 0"
      class="py-8 px-4 text-center text-sm text-slate_500 poppinsMedium"
    >
      No recordings were captured for this attempt.
    </p>

    <div
      *ngIf="visibleFeedCount > 0"
      class="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5"
    >
      <div
        class="flex flex-col gap-3 min-w-0"
        *ngIf="
          driveAssessmentData?.urlsForResponse?.WEB_FEED &&
          mergeVideoStatus?.WEB_FEED != 'NoChunks'
        "
      >
        <div class="flex items-center gap-2">
          <img [src]="_commonServices.Images.FEED_MONITOR" alt="" class="flex-shrink-0" />
          <span class="text-[15px] leading-[22px] text-slate_600 poppinsSemiBold">Primary feed</span>
        </div>
        <div class="relative">
          <video
            *ngIf="isFeedReady('WEB_FEED'); else feedProcessing"
            controls
            preload="metadata"
            class="rounded-lg w-full aspect-video bg-video_frame_bg"
            [src]="driveAssessmentData?.urlsForResponse?.WEB_FEED"
            type="video/mp4"
          >
            Your browser does not support the video tag.
          </video>
        </div>
        <button
          type="button"
          (click)="unMergedChunksFiles('WEB_FEED')"
          class="self-start flex items-center gap-2 py-2 px-3 bg-white border border-slate_300 shadow-card-sm rounded-lg text-sm leading-5 text-dark_blue poppinsMedium hover:bg-slate_50 transition-colors"
        >
          Unmerged web feeds
        </button>
      </div>

      <div
        class="flex flex-col gap-3 min-w-0"
        *ngIf="
          driveAssessmentData?.urlsForResponse?.MOBILE_FEED &&
          mergeVideoStatus?.MOBILE_FEED != 'NoChunks'
        "
      >
        <div class="flex items-center gap-2">
          <img [src]="_commonServices.Images.FEED_PHONE" alt="" class="flex-shrink-0" />
          <span class="text-[15px] leading-[22px] text-slate_600 poppinsSemiBold">Secondary feed</span>
        </div>
        <div class="relative">
          <video
            *ngIf="isFeedReady('MOBILE_FEED'); else feedProcessing"
            controls
            preload="metadata"
            class="rounded-lg w-full aspect-video bg-video_frame_bg"
            [src]="driveAssessmentData?.urlsForResponse?.MOBILE_FEED"
            type="video/mp4"
          >
            Your browser does not support the video tag.
          </video>
        </div>
      </div>

      <div
        class="flex flex-col gap-3 min-w-0"
        *ngIf="
          driveAssessmentData?.urlsForResponse?.SCREEN_SHARE &&
          mergeVideoStatus?.SCREEN_SHARE != 'NoChunks'
        "
      >
        <div class="flex items-center gap-2">
          <img [src]="_commonServices.Images.FEED_SCREEN" alt="" class="flex-shrink-0" />
          <span class="text-[15px] leading-[22px] text-slate_600 poppinsSemiBold">Screen recording</span>
        </div>
        <div class="relative">
          <video
            *ngIf="isFeedReady('SCREEN_SHARE'); else feedProcessing"
            controls
            preload="metadata"
            class="rounded-lg w-full aspect-video bg-video_frame_bg"
            [src]="driveAssessmentData?.urlsForResponse?.SCREEN_SHARE"
            type="video/mp4"
          >
            Your browser does not support the video tag.
          </video>
        </div>
        <button
          type="button"
          (click)="unMergedChunksFiles('SCREEN_SHARE')"
          class="self-start flex items-center gap-2 py-2 px-3 bg-white border border-slate_300 shadow-card-sm rounded-lg text-sm leading-5 text-dark_blue poppinsMedium hover:bg-slate_50 transition-colors"
        >
          Unmerged screen feeds
        </button>
      </div>

      <div
        class="flex flex-col gap-3 min-w-0"
        *ngIf="
          driveAssessmentData?.urlsForResponse?.SURROUNDING_FEED &&
          mergeVideoStatus?.SURROUNDING_FEED != 'NoChunks'
        "
      >
        <div class="flex items-center gap-2">
          <img [src]="_commonServices.Images.FEED_PHONE" alt="" class="flex-shrink-0" />
          <span class="text-[15px] leading-[22px] text-slate_600 poppinsSemiBold">Surrounding feed</span>
        </div>
        <video
          *ngIf="isFeedReady('SURROUNDING_FEED'); else feedProcessing"
          controls
          preload="metadata"
          class="rounded-lg w-full aspect-video bg-video_frame_bg"
          [src]="driveAssessmentData?.urlsForResponse?.SURROUNDING_FEED"
          type="video/mp4"
        >
          Your browser does not support the video tag.
        </video>
      </div>
      <div class="flex flex-col gap-3 min-w-0" *ngIf="driveAssessmentData?.urlsForResponse?.AUDIO_FEED">
        <div class="flex items-center gap-2">
          <span class="text-[15px] leading-[22px] text-slate_600 poppinsSemiBold">Audio</span>
        </div>
        <video
          controls
          preload="metadata"
          class="rounded-lg w-full aspect-video bg-video_frame_bg"
          [src]="driveAssessmentData?.urlsForResponse?.AUDIO_FEED"
          type="video/mp4"
        >
          Your browser does not support the video tag.
        </video>
      </div>
    </div>
  </div>

  <div class="flex flex-col gap-5 mt-5" *ngIf="reportKind !== 'sr'">
    <!-- Popup Questions -->
    <div
      *ngIf="!isQuestionExplanationResponse && popupQuestions.length"
      class="bg-white border border-slate_200 rounded-[10px] shadow-card-section overflow-hidden"
    >
      <div class="flex items-center gap-2 py-3.5 px-4 border-b border-slate_200">
        <span class="w-1 h-6 rounded-[1px_2px_2px_1px] bg-health-yellow-gradient"></span>
        <h2 class="text-base leading-[140%] text-slate_700 poppinsSemiBold">
          Popup Questions
        </h2>
      </div>
      <div
        class="p-4 max-h-[31.25rem] overflow-y-auto changeScrollbarStyle grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5"
      >
        <div *ngFor="let item of popupQuestions" class="flex flex-col gap-3 min-w-0">
          <video
            controls
            preload="metadata"
            class="rounded-lg w-full aspect-video bg-video_frame_bg"
            [src]="item?.selectedRandomQuestionExplainedUrl"
            type="video/mp4"
          >
            Your browser does not support the video tag.
          </video>
          <div class="flex flex-col gap-1.5">
            <p class="text-sm leading-[21px]">
              <span class="text-dark_blue poppinsSemiBold">Section: </span>
              <span class="text-my_profile_secondary_text poppinsMedium">{{
                item?.sectionName || "-"
              }}</span>
            </p>
            <p class="text-sm leading-[21px]">
              <span class="text-dark_blue poppinsSemiBold">Question: </span>
              <span class="text-my_profile_secondary_text poppinsMedium">{{
                item?.questionText || "-"
              }}</span>
            </p>
            <p class="text-sm leading-[21px]">
              <span class="text-dark_blue poppinsSemiBold">Response: </span>
              <span class="text-my_profile_secondary_text poppinsMedium">{{
                item?.seletedAnswer || "-"
              }}</span>
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Security Events -->
    <div
      *ngIf="driveAssessmentData?.userDto?.securityEvents"
      class="bg-white border border-slate_200 rounded-[10px] shadow-card-section overflow-hidden"
    >
      <div class="flex items-center gap-2 py-3.5 px-4 border-b border-slate_200">
        <span class="w-1 h-6 rounded-[1px_2px_2px_1px] bg-health-yellow-gradient"></span>
        <h2 class="text-base leading-[140%] text-slate_700 poppinsSemiBold">
          Security Events
        </h2>
      </div>
      <div class="overflow-x-auto">
        <table class="report-table w-full border-collapse min-w-[32rem]">
          <thead class="bg-light_blue uppercase poppinsSemiBold text-sm text-dark_blue tracking-[0.025em]">
            <tr>
              <th scope="col" class="p-4 text-left border-b border-slate_300 whitespace-nowrap w-1/2">Event</th>
              <th scope="col" class="p-4 text-left border-b border-slate_300 whitespace-nowrap">Value</th>
            </tr>
          </thead>
          <tbody>
            <tr
              *ngFor="let item of driveAssessmentData?.userDto?.securityEvents"
              class="border-b border-grey_150 last:border-b-0"
            >
              <td class="py-[18px] px-4 text-[15px] poppinsMedium text-dark_navy">
                {{ item?.key ?? "-" }}
              </td>
              <td
                class="py-[18px] px-4 text-[15px] poppinsMedium text-my_profile_secondary_text max-w-0 truncate"
                [title]="item?.value"
              >
                {{ item?.value ?? "-" }}
                <span *ngIf="item?.relativeTimeStamp?.length" class="text-sm text-slate_400">
                  (at {{ item.relativeTimeStamp.join(", ") }})
                </span>
              </td>
            </tr>
            <tr *ngIf="!driveAssessmentData?.userDto?.securityEvents?.length">
              <td colspan="2" class="py-8 px-4 text-center text-sm text-slate_500 poppinsMedium">
                No security events were recorded.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <!-- <div *ngIf="detectedMonitorData.length>0">
      <p class="text-primary_Text_Color text-lg font-medium mt-10 mb-5">
        Monitoring Table
      </p>
      <div class="border border-card_Bg_Color rounded-xl w-full shadow-md">
        <table class="table-fixed w-full">
          <thead>
            <tr class="border-b border-card_Bg_Color grid grid-cols-2 tracking-wider">
              <th class="text-secondary_Light_Text_Color text-xs px-5 py-3 text-start pl-[24%] pr-5">
               EVENT NAME
              </th>
              <th class="text-secondary_Light_Text_Color text-xs px-5 py-3 text-start pl-[28%] pr-5">
                TIME
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              [ngClass]="detectedMonitorData.length-1 !== i ? 'border-b border-card_Bg_Color grid grid-cols-2 tracking-wider' : 'grid grid-cols-2 tracking-wider'"
              *ngFor="let item of detectedMonitorData ;index as i">
              <td class="text-primary_Text_Color text-base font-medium text-start whitespace-nowrap pl-[20%] pr-5 py-3">
                {{
                  item?.appName == null
                  ? "-"
                  : item?.appName
                  }}
              </td>
              <td
                class="text-primary_Text_Color text-base font-medium text-start whitespace-nowrap pl-[20%] pr-5 py-3 overflow-hidden text-ellipsis inline-block align-middle truncate">
                {{
                item?.createdOn == null
                ? "-"
                : item?.createdOn |date:'MMM d, y, h:mm:ss a'
                }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div> -->
  </div>

  <!-- AI Proctoring results -->
  <!-- <div class="mb-5 mt-10" *ngIf="driveAssessmentData?.userDto?.sectionScoreDtoList.length > 0">
    <p class="text-primary_Text_Color text-lg font-medium my-5">
      AI Proctoring results:
    </p>
    <div class="border border-card_Bg_Color rounded-xl w-full">
      <table class="table-fixed w-full">
        <thead>
          <tr class="border-b border-card_Bg_Color grid grid-cols-3 tracking-wider">
            <th class="text-secondary_Light_Text_Color text-xs px-5 py-3 text-start pl-[25%] pr-5 uppercase">
              S.No
            </th>
            <th class="text-secondary_Light_Text_Color text-xs px-5 py-3 text-start pl-[18%] pr-5 uppercase">
              Time Stamp
            </th>
            <th class="text-secondary_Light_Text_Color text-xs px-5 py-3 text-start pl-[18%] pr-5 uppercase">
              whats wrong
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            [ngClass]="driveAssessmentData?.userDto?.securityEvents.length-1 !== i ? 'border-b border-card_Bg_Color grid grid-cols-3 tracking-wider' : 'grid grid-cols-3 tracking-wider'"
            *ngFor="let item of driveAssessmentData?.userDto?.securityEvents ;index as i">
            <td class="text-my_profile_secondary_text font-medium whitespace-nowrap pl-[25%] pr-5 py-3">
              {{ i+1 }}
            </td>

            <td class="text-blue_Color underline font-medium whitespace-nowrap pl-[18%] pr-5 py-3">
              1:20
            </td>
            <td
              class="text-my_profile_secondary_text font-medium whitespace-nowrap pl-[18%] pr-5 py-3 overflow-hidden text-ellipsis inline-block align-middle truncate">
              {{
              item?.value == null
              ? "-"
              : item?.value
              }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div> -->

  <div *ngIf="driveAssessmentData?.videoSectionResponse?.length > 0">
    <p class="text-primary_Text_Color text-lg font-medium my-6">
      Video Assessments
    </p>
    <!-- <div class="grid grid-cols-4 gap-4">
      <div *ngFor="let video of driveAssessmentData?.videoSectionResponse">
        <div
          *ngFor="let videoRecord of video?.videoQuestionResponse"
          class="bg-white shadow-md rounded-md"
        >
          <div>
            <video
              controls
              class="rounded-lg fixed-dimensions "
              [src]="videoRecord?.videoUrl"
              type="video/mp4"
            >
              Your browser does not support the video tag.
            </video>
            <p class="text-gray_text py-2 px-4">
              Section :
              <span class="text-secondary_Light_Text_Color">
                {{ video?.sectionName }}</span
              >
            </p>
            <p
              class="text-gray_text px-4 pb-2 inline-block align-middle w-full overflow-hidden truncate text-ellipsis"
              [title]="
                videoRecord?.questionText == null
                  ? '-'
                  : videoRecord?.questionText
              "
            >
              Question :
              <span class="text-gray_secondary text-wrap">{{
                videoRecord?.questionText == null
                  ? "-"
                  : videoRecord?.questionText
              }}</span>
            </p>
          </div>
        </div>
      </div>
    </div> -->

    <div class="container mx-auto">
      <!-- Loop through each video section -->
      <div
        *ngFor="let video of driveAssessmentData?.videoSectionResponse"
        class="mb-6"
      >
        <!-- Loop through each video record within a section -->
        <div
          *ngFor="let videoRecord of video?.videoQuestionResponse"
          class="flex flex-row bg-white shadow-card-section rounded-md mb-4"
        >
          <!-- Video container (40% width) -->
          <div class="w-2/5 p-2">
            <video
              *ngIf="videoRecord?.videoUrl; else noVideoAnswer"
              controls
              class="rounded-lg w-full h-auto"
              [src]="videoRecord?.videoUrl"
              type="video/mp4"
            >
              Your browser does not support the video tag.
            </video>
          </div>

          <!-- Question container (60% width) -->
          <div class="w-3/5 p-4 flex flex-col justify-center">
            <p class="text-gray_text mb-2">
              Section:
              <span class="text-secondary_Light_Text_Color">{{
                video?.sectionName
              }}</span>
            </p>
            <p class="text-gray_text">
              Question:
              <span class="text-gray_secondary">
                {{
                  videoRecord?.questionText == null
                    ? "-"
                    : videoRecord?.questionText
                }}
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div *ngIf="driveAssessmentData?.audioSectionResponse?.length > 0">
    <p class="text-primary_Text_Color text-lg font-medium my-6">
      Audio Assessments
    </p>
    <div class="grid grid-cols-3 xl:grid-cols-4 gap-4">
      <ng-container
        *ngFor="let video of driveAssessmentData?.audioSectionResponse"
      >
        <ng-container
          *ngFor="
            let audioRecord of video?.videoQuestionResponse;
            let i = index
          "
        >
          <div
            class="bg-white shadow-lg rounded-md col-span-1"
            [ngClass]="
              audioRecord?.videoUrl == null
                ? 'cursor-not-allowed opacity-60'
                : ''
            "
          >
            <div>
              <img
                [src]="
                  audioPlayer?.paused
                    ? _commonServices.Images.PLAY_AUDIO_ICON
                    : _commonServices.Images.PAUSE_AUDIO_ICON
                "
                alt="Audio Control"
                class="mt-5 mx-auto cursor-pointer w-1/2"
                (click)="toggleAudio(audioPlayer)"
              />
              <audio
                #audioPlayer
                [src]="audioRecord?.videoUrl"
                class="hidden"
                controls
                muted
                (ended)="audioEnded(audioPlayer)"
              ></audio>
              <p
                [ngClass]="
                  audioRecord?.videoUrl == null
                    ? 'cursor-not-allowed'
                    : 'cursor-pointer'
                "
                (click)="toggleAudio(audioPlayer)"
                class="text-sm underline -mt-4 text-center"
              >
                {{ audioPlayer?.paused ? "Play Audio" : "Pause Audio" }}
              </p>
              <p class="text-gray_text py-2 px-4">
                Section :
                <span class="text-secondary_Light_Text_Color">{{
                  video?.sectionName
                }}</span>
              </p>
              <p
                class="text-gray_text px-4 pb-2 inline-block align-middle w-full overflow-hidden truncate text-ellipsis"
                [title]="
                  audioRecord?.questionText == null
                    ? '-'
                    : audioRecord?.questionText
                "
              >
                Question :
                <span class="text-gray_secondary">
                  {{
                    audioRecord?.questionText == null
                      ? "-"
                      : audioRecord?.questionText
                  }}
                </span>
              </p>
            </div>
          </div>
        </ng-container>
      </ng-container>
    </div>
  </div>

  <div *ngIf="driveReportResponseList?.length">
    <h1 class="text-primary_Text_Color text-lg font-medium mb-4 mt-8">
      Assessment Response
    </h1>
  </div>
  <div class="pb-8">
    <div class="grid grid-cols-1  gap-4">
      <div
        *ngFor="let item of driveReportResponseList"
        class="border rounded-lg shadow-sm p-6 bg-white space-y-2"
      >
        <div class="flex justify-between items-start">
          <div>
            <p class="text-xs text-gray-500">Question ID</p>
            <p class="font-medium">{{ item?.questionID }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-500">Category</p>
            <p class="font-medium">{{ item?.category }}</p>
          </div>

          <div>
            <p class="text-xs text-gray-500">Difficulty Level</p>
            <p class="font-medium">{{ item?.difficultyLevel }}</p>
          </div>
          <div *ngIf="!isMediaQuestion(item) && item?.answered !== 'N' && item?.correctlyAnswered !== 'N'">
            <span>
              <img [src]="_commonServices.Images.APPROVE_ICON" alt="right" width="30" height="30" />
            </span>
          </div>
          
          
          <div *ngIf="!isMediaQuestion(item) && item?.answered === 'Y' && item?.correctlyAnswered === 'N'">
            <span>
              <img [src]="_commonServices.Images.CROSS_CIRCLE" alt="wrong" width="30" height="30" />
            </span>
          </div>
          
          <div *ngIf="item?.answered === 'N' ">
            <span>
              <img [src]="_commonServices.Images.INFO" alt="allow" width="30" height="30" />
            </span>
          </div>


        </div>

        <div>
          <p class="text-xs text-gray-500">Question Text</p>
          <p class="line-clamp-2 text-sm" [title]="item?.questionText">
            {{ item?.questionText }}
          </p>
        </div>

        <div *ngIf="!isMediaQuestion(item)">
          <p class="text-xs text-gray-500">Selected Answer</p>
          <p class="text-sm">{{ answerGiven(item) ?? "Not answered" }}</p>
        </div>
        <div *ngIf="item?.correctlyAnswered !== 'Y' && expectedAnswer(item) != null">
          <p class="text-xs text-gray-500">Correct Answer</p>
          <p class="text-sm">{{ expectedAnswer(item) }}</p>
        </div>
        <div class="flex gap-6 items-start">
          <div>
            <p class="text-xs text-gray-500">Marks Scored</p>
            <p class="text-sm">
              {{ item?.marksScored != null && item?.maxMarks != null ? item.marksScored + '/' + item.maxMarks : '-' }}
            </p>
          </div>
          <div>
            <p class="text-xs text-gray-500">Time Spent (s)</p>
            <p class="text-sm">{{ item?.timeSpentOnQuestion ?? 0 }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</ng-container>

<ng-template #emptyCell><span class="is-empty">-</span></ng-template>

<!-- Video question the candidate did not record (videoUrl is null) -->
<ng-template #noVideoAnswer>
  <div class="rounded-lg w-full aspect-video bg-slate_100 border border-slate_200 flex items-center justify-center px-4 text-center">
    <p class="text-sm text-slate_500 poppinsMedium">No answer was recorded for this question.</p>
  </div>
</ng-template>

<!-- Feed tile body while the recording is still being merged -->
<ng-template #feedProcessing>
  <div class="rounded-lg w-full aspect-video bg-slate_100 border border-slate_200 flex items-center justify-center px-4 text-center">
    <p class="text-sm text-slate_500 poppinsMedium">The recording is still being processed.</p>
  </div>
</ng-template>

<!-- Chat bubbles shared by the bot-stage and final-summary transcripts -->
<ng-template #transcriptBody let-messages>
  <div class="flex flex-col gap-4 p-5 max-h-[40rem] overflow-y-auto changeScrollbarStyle">
    <ng-container *ngFor="let msg of messages">
      <div *ngIf="isBotMessage(msg)" class="flex items-end gap-3">
        <span class="w-9 h-9 shrink-0 rounded-full bg-blue_Color flex items-center justify-center text-white poppinsBold text-[13px]">IB</span>
        <div class="max-w-[75%] bg-white border border-slate_200 shadow-card-sm rounded-[12px_12px_12px_4px] px-4 py-3">
          <p class="text-xs poppinsSemiBold uppercase tracking-[0.03em] text-blue_Color">Interview bot</p>
          <p class="mt-1 text-sm leading-[24px] poppinsMedium text-dark_blue whitespace-pre-line">{{ msg?.message }}</p>
        </div>
      </div>
      <div *ngIf="isCandidateMessage(msg)" class="flex items-end justify-end gap-3">
        <div class="max-w-[75%] bg-slate_50 border border-slate_200 shadow-card-sm rounded-[12px_12px_4px_12px] px-4 py-3">
          <p class="text-xs poppinsSemiBold uppercase tracking-[0.03em] text-Active_Otp_Border">{{ candidateName || "Candidate" }}</p>
          <p class="mt-1 text-sm leading-[24px] poppinsMedium text-dark_blue whitespace-pre-line">{{ msg?.message }}</p>
        </div>
        <span class="w-9 h-9 shrink-0 rounded-full bg-Active_Otp_Border flex items-center justify-center text-white poppinsBold text-[13px]">{{ getInitials(candidateName) }}</span>
      </div>
    </ng-container>
    <p *ngIf="!messages?.length" class="py-6 text-center text-sm text-slate_500 poppinsMedium">
      No transcript was recorded for this interview.
    </p>
  </div>
</ng-template>

<!-- ═══ Interview-bot stage ═══════════════════════════════════════════ -->
<ng-template #botStage>
  <ng-container *ngIf="driveAssessmentData?.userDto as user">
    <div class="grid grid-cols-1 xl:grid-cols-2 gap-5">
      <div class="bg-white border border-slate_200 rounded-[10px] shadow-card-section overflow-hidden">
        <div class="flex flex-wrap items-center justify-between gap-2 py-3.5 px-4 border-b border-slate_200">
          <div class="flex items-center gap-2">
            <span class="w-1 h-6 rounded-[1px_2px_2px_1px] bg-health-yellow-gradient"></span>
            <h2 class="text-base leading-[140%] text-slate_700 poppinsSemiBold">Candidate Information</h2>
          </div>
        </div>
        <div class="info-row" [title]="user?.candidateId">
          <span class="info-row__icon bg-blue_50">
            <img [src]="_commonServices.Images.CANDIDATE_INFO_ID_CARD" alt="" />
          </span>
          <span class="info-row__label text-dark_blue poppinsSemiBold">Candidate ID</span>
          <span class="info-row__value text-my_profile_secondary_text poppinsMedium">{{ user?.candidateId || "-" }}</span>
        </div>
        <div class="info-row" [title]="user?.email">
          <span class="info-row__icon bg-blue_50">
            <img [src]="_commonServices.Images.CANDIDATE_INFO_MAIL" alt="" />
          </span>
          <span class="info-row__label text-dark_blue poppinsSemiBold">Email</span>
          <span class="info-row__value text-my_profile_secondary_text poppinsMedium">{{ user?.email || "-" }}</span>
        </div>
        <div class="info-row">
          <span class="info-row__icon bg-blue_50">
            <img [src]="_commonServices.Images.CANDIDATE_INFO_PHONE" alt="" />
          </span>
          <span class="info-row__label text-dark_blue poppinsSemiBold">Phone</span>
          <span class="info-row__value text-my_profile_secondary_text poppinsMedium">{{ cleanPhone(user?.mobileNumber) || "-" }}</span>
        </div>
        <div class="info-row">
          <span class="info-row__icon bg-blue_50">
            <img [src]="_commonServices.Images.CANDIDATE_INFO_ID_CARD" alt="" />
          </span>
          <span class="info-row__label text-dark_blue poppinsSemiBold">Drive ID</span>
          <span class="info-row__value text-my_profile_secondary_text poppinsMedium">{{ driveAssessmentData?.driveId || "-" }}</span>
        </div>
      </div>

      <!-- Proctoring, straight from proctoringDTO -->
      <div
        *ngIf="driveAssessmentData?.proctoringDTO as proc"
        class="bg-white border border-slate_200 rounded-[10px] shadow-card-section overflow-hidden"
      >
        <div class="flex flex-wrap items-center justify-between gap-2 py-3.5 px-4 border-b border-slate_200">
          <div class="flex items-center gap-2">
            <span class="w-1 h-6 rounded-[1px_2px_2px_1px] bg-health-yellow-gradient"></span>
            <h2 class="text-base leading-[140%] text-slate_700 poppinsSemiBold">Proctoring</h2>
          </div>
        </div>
        <div class="info-row">
          <span class="info-row__icon" [ngClass]="(proc?.countWindowsSwitched > TAB_SWITCH_LIMIT) ? 'bg-red_50' : 'bg-green_100'">
            <img
              [src]="(proc?.countWindowsSwitched > TAB_SWITCH_LIMIT) ? _commonServices.Images.STATUS_WARNING_TRIANGLE : _commonServices.Images.STATUS_CHECK_CIRCLE"
              alt=""
            />
          </span>
          <span class="info-row__label text-dark_blue poppinsSemiBold">Window switches</span>
          <span
            class="info-row__value poppinsMedium"
            [ngClass]="(proc?.countWindowsSwitched > TAB_SWITCH_LIMIT) ? 'text-red_600' : 'text-green_600'"
          >{{ proc?.countWindowsSwitched ?? 0 }}</span>
        </div>
        <div class="info-row">
          <span class="info-row__icon" [ngClass]="(proc?.violationMade || proc?.countViolationMade > 0) ? 'bg-red_50' : 'bg-green_100'">
            <img
              [src]="(proc?.violationMade || proc?.countViolationMade > 0) ? _commonServices.Images.STATUS_WARNING_TRIANGLE : _commonServices.Images.STATUS_CHECK_CIRCLE"
              alt=""
            />
          </span>
          <span class="info-row__label text-dark_blue poppinsSemiBold">Violations</span>
          <span
            class="info-row__value poppinsMedium"
            [ngClass]="(proc?.violationMade || proc?.countViolationMade > 0) ? 'text-red_600' : 'text-green_600'"
          >{{ proc?.countViolationMade ?? 0 }}</span>
        </div>
        <div class="info-row">
          <span class="info-row__icon" [ngClass]="proc?.backgroundAppDetected ? 'bg-red_50' : 'bg-green_100'">
            <img
              [src]="proc?.backgroundAppDetected ? _commonServices.Images.STATUS_WARNING_TRIANGLE : _commonServices.Images.STATUS_CHECK_CIRCLE"
              alt=""
            />
          </span>
          <span class="info-row__label text-dark_blue poppinsSemiBold">Background apps</span>
          <span
            class="info-row__value poppinsMedium"
            [ngClass]="proc?.backgroundAppDetected ? 'text-red_600' : 'text-green_600'"
          >{{ detectionLabel(proc?.backgroundAppDetected, driveAssessmentData?.backgroundAppDetectedList, 'Not detected') }}</span>
        </div>
        <div class="info-row">
          <span class="info-row__icon" [ngClass]="proc?.externalHardware ? 'bg-red_50' : 'bg-green_100'">
            <img
              [src]="proc?.externalHardware ? _commonServices.Images.STATUS_WARNING_TRIANGLE : _commonServices.Images.STATUS_CHECK_CIRCLE"
              alt=""
            />
          </span>
          <span class="info-row__label text-dark_blue poppinsSemiBold">External hardware</span>
          <span
            class="info-row__value poppinsMedium"
            [ngClass]="proc?.externalHardware ? 'text-red_600' : 'text-green_600'"
          >{{ detectionLabel(proc?.externalHardware, driveAssessmentData?.externalHardwareList, 'Not detected') }}</span>
        </div>
      </div>
    </div>
  </ng-container>

  <!-- Interview topic scores (0-10 each) -->
  <div class="bg-white border border-slate_200 rounded-[10px] shadow-card-section overflow-hidden">
        <div class="flex flex-wrap items-center justify-between gap-2 py-3.5 px-4 border-b border-slate_200">
          <div class="flex items-center gap-2">
            <span class="w-1 h-6 rounded-[1px_2px_2px_1px] bg-health-yellow-gradient"></span>
            <h2 class="text-base leading-[140%] text-slate_700 poppinsSemiBold">Interview Topic Scores</h2>
          </div>
          <span *ngIf="botTotal as total" class="text-sm text-slate_500 poppinsMedium">
            Overall <span class="text-dark_navy poppinsSemiBold">{{ fmt(total.score) }} / {{ total.max }}</span>
          </span>
        </div>
    <p
      *ngIf="driveAssessmentData?.isInterviewBotReportGenerated === false"
      class="py-3 px-5 bg-slate_50 border-b border-slate_200 text-sm text-slate_500 poppinsMedium"
    >
      The detailed interview report is not ready yet.<ng-container *ngIf="botTopics.length">
        Topic scores are shown below.</ng-container>
    </p>
    <ul *ngIf="botTopics.length; else noTopics">
      <li
        *ngFor="let topic of botTopics"
        class="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,16rem)_4.5rem] items-center gap-x-6 gap-y-2 py-[18px] px-5 border-b border-grey_150 last:border-b-0"
      >
        <span class="text-[15px] leading-[140%] poppinsMedium text-dark_navy first-letter:uppercase">
          {{ topic?.topicName || "-" }}
        </span>
        <span class="h-2 rounded-full bg-slate_100 overflow-hidden" aria-hidden="true">
          <span
            class="block h-full rounded-full"
            [ngClass]="topicTone(topic?.score)"
            [style.width.%]="topicBarWidth(topic?.score)"
          ></span>
        </span>
        <span class="text-[15px] poppinsSemiBold text-dark_navy sm:text-right tabular-nums">
          {{ fmt(topic?.score) }}<span class="text-slate_400 poppinsMedium"> / {{ BOT_TOPIC_MAX }}</span>
        </span>
      </li>
    </ul>
    <ng-template #noTopics>
      <p class="py-8 px-4 text-center text-sm text-slate_500 poppinsMedium">
        No topic scores have been recorded for this interview yet.
      </p>
    </ng-template>
  </div>

  <!-- Interview transcript (fetched by callId) -->
  <div
    *ngIf="driveAssessmentData?.callId"
    class="bg-white border border-slate_200 rounded-[10px] shadow-card-section overflow-hidden"
  >
    <div class="flex flex-wrap items-center justify-between gap-2 py-3.5 px-4 border-b border-slate_200">
      <div class="flex items-center gap-2">
        <span class="w-1 h-6 rounded-[1px_2px_2px_1px] bg-health-yellow-gradient"></span>
        <h2 class="text-base leading-[140%] text-slate_700 poppinsSemiBold">Interview Transcript</h2>
      </div>
      <span *ngIf="botTranscriptState === 'ready' && botConversation.length" class="text-sm text-slate_500 poppinsMedium">
        {{ botConversation.length }} messages
      </span>
    </div>
    <p *ngIf="botTranscriptState === 'loading'" class="py-8 px-4 text-center text-sm text-slate_500 poppinsMedium">
      Loading transcript&hellip;
    </p>
    <p *ngIf="botTranscriptState === 'failed'" class="py-8 px-4 text-center text-sm text-slate_500 poppinsMedium">
      The transcript could not be loaded. Refresh the page to try again.
    </p>
    <ng-container
      *ngIf="botTranscriptState === 'ready'"
      [ngTemplateOutlet]="transcriptBody"
      [ngTemplateOutletContext]="{ $implicit: botConversation }"
    ></ng-container>
  </div>
</ng-template>

<!-- ═══ SR report: one section per stage, picked by data.reportType ═══════ -->
<ng-template #srReport>
  <!-- Identity comes from finalSummary on a final report, else resumeMatching -->
  <div class="grid grid-cols-1 gap-5" [ngClass]="{ 'xl:grid-cols-2': finalSummary }">
    <div class="bg-white border border-slate_200 rounded-[10px] shadow-card-section overflow-hidden">
        <div class="flex flex-wrap items-center justify-between gap-2 py-3.5 px-4 border-b border-slate_200">
          <div class="flex items-center gap-2">
            <span class="w-1 h-6 rounded-[1px_2px_2px_1px] bg-health-yellow-gradient"></span>
            <h2 class="text-base leading-[140%] text-slate_700 poppinsSemiBold">Candidate Information</h2>
          </div>
        </div>
        <div class="info-row">
          <span class="info-row__icon bg-blue_50">
            <img [src]="_commonServices.Images.CANDIDATE_INFO_ID_CARD" alt="" />
          </span>
          <span class="info-row__label text-dark_blue poppinsSemiBold">Candidate ID</span>
          <span class="info-row__value text-my_profile_secondary_text poppinsMedium">{{ srCandidate?.candidateId || "-" }}</span>
        </div>
        <div class="info-row" [title]="srCandidate?.email || ''">
          <span class="info-row__icon bg-blue_50">
            <img [src]="_commonServices.Images.CANDIDATE_INFO_MAIL" alt="" />
          </span>
          <span class="info-row__label text-dark_blue poppinsSemiBold">Email</span>
          <span class="info-row__value text-my_profile_secondary_text poppinsMedium">{{ srCandidate?.email || "-" }}</span>
        </div>
        <div class="info-row">
          <span class="info-row__icon bg-blue_50">
            <img [src]="_commonServices.Images.CANDIDATE_INFO_PHONE" alt="" />
          </span>
          <span class="info-row__label text-dark_blue poppinsSemiBold">Phone</span>
          <span class="info-row__value text-my_profile_secondary_text poppinsMedium">{{ cleanPhone(srCandidate?.phone) || "-" }}</span>
        </div>
        <div class="info-row" [title]="srCandidate?.jobApplicationId || ''">
          <span class="info-row__icon bg-blue_50">
            <img [src]="_commonServices.Images.CANDIDATE_INFO_ID_CARD" alt="" />
          </span>
          <span class="info-row__label text-dark_blue poppinsSemiBold">Application ID</span>
          <span class="info-row__value text-my_profile_secondary_text poppinsMedium">{{ srCandidate?.jobApplicationId || "-" }}</span>
        </div>
        <div *ngIf="finalSummary" class="info-row" [title]="finalSummary?.recruiter || ''">
          <span class="info-row__icon bg-blue_50">
            <img [src]="_commonServices.Images.CANDIDATE_INFO_MAIL" alt="" />
          </span>
          <span class="info-row__label text-dark_blue poppinsSemiBold">Recruiter</span>
          <span class="info-row__value text-my_profile_secondary_text poppinsMedium">{{ finalSummary?.recruiter || "-" }}</span>
        </div>
    </div>

    <div *ngIf="finalSummary" class="flex flex-col gap-5">
      <div class="bg-white border border-slate_200 rounded-[10px] shadow-card-section overflow-hidden">
        <div class="flex flex-wrap items-center justify-between gap-2 py-3.5 px-4 border-b border-slate_200">
          <div class="flex items-center gap-2">
            <span class="w-1 h-6 rounded-[1px_2px_2px_1px] bg-health-yellow-gradient"></span>
            <h2 class="text-base leading-[140%] text-slate_700 poppinsSemiBold">Skills</h2>
          </div>
        </div>
        <div class="flex flex-wrap gap-2 p-4">
          <span
            *ngFor="let skill of finalSummary?.skills"
            class="poppinsMedium text-xs text-skill_tag_text bg-light_yellow border border-cutoff_card_border rounded-md py-[5px] px-2.5"
          >{{ skill }}</span>
          <span *ngIf="!finalSummary?.skills?.length" class="poppinsMedium text-sm text-slate_400">
            No skills on record
          </span>
        </div>
      </div>
      <div class="bg-white border border-slate_200 rounded-[10px] shadow-card-section overflow-hidden">
        <div class="flex flex-wrap items-center justify-between gap-2 py-3.5 px-4 border-b border-slate_200">
          <div class="flex items-center gap-2">
            <span class="w-1 h-6 rounded-[1px_2px_2px_1px] bg-health-yellow-gradient"></span>
            <h2 class="text-base leading-[140%] text-slate_700 poppinsSemiBold">Other SRs</h2>
          </div>
        </div>
        <div class="flex flex-wrap gap-2 p-4">
          <span
            *ngFor="let sr of finalSummary?.otherSrNumbers"
            class="poppinsMedium text-xs text-blue_Color bg-sr_chip_bg border border-blue_40 rounded-md py-[5px] px-2.5"
          >{{ sr }}</span>
          <span *ngIf="!finalSummary?.otherSrNumbers?.length" class="poppinsMedium text-sm text-slate_400">
            Not part of any other SR
          </span>
        </div>
      </div>
    </div>
  </div>

  <!-- finalSummary is null until the Workday application and app user exist -->
  <ng-container *ngIf="finalSummary">
  <!-- Stage scorecard: same columns and total-row mapping as the SR candidate scorecard -->
  <div class="bg-white border border-slate_200 rounded-[10px] shadow-card-section overflow-hidden">
        <div class="flex flex-wrap items-center justify-between gap-2 py-3.5 px-4 border-b border-slate_200">
          <div class="flex items-center gap-2">
            <span class="w-1 h-6 rounded-[1px_2px_2px_1px] bg-health-yellow-gradient"></span>
            <h2 class="text-base leading-[140%] text-slate_700 poppinsSemiBold">Stage Scores</h2>
          </div>
        </div>
    <div class="overflow-x-auto">
      <table class="report-table w-full border-collapse min-w-[52rem]">
        <thead class="bg-light_blue uppercase poppinsSemiBold text-sm text-dark_blue tracking-[0.025em]">
          <tr>
            <th scope="col" class="p-4 text-left border-b border-slate_300 whitespace-nowrap">Stage</th>
            <th scope="col" class="p-4 text-center border-b border-slate_300 whitespace-nowrap">Weight</th>
            <th scope="col" class="p-4 text-center border-b border-slate_300 whitespace-nowrap">Score</th>
            <th scope="col" class="p-4 text-left border-b border-slate_300 whitespace-nowrap">Proctoring</th>
            <th scope="col" class="p-4 text-center border-b border-slate_300 whitespace-nowrap">Achieved</th>
            <th scope="col" class="p-4 text-center border-b border-slate_300 whitespace-nowrap">Weighted score</th>
          </tr>
        </thead>
        <tbody>
          <tr
            *ngFor="let stage of finalSummary?.evaluationScores; index as i"
            class="border-b border-grey_150"
          >
            <td class="py-[18px] px-4">
              <span class="block text-[15px] leading-[140%] poppinsSemiBold text-dark_navy">
                {{ stage?.evaluationStage || "-" }}
              </span>
              <span class="inline-block mt-1 text-[11px] leading-4 poppinsMedium text-slate_500 bg-slate_50 border border-slate_200 rounded-md py-0.5 px-2">
                Stage {{ i + 1 }}
              </span>
            </td>
            <td class="py-[18px] px-4 text-center text-[15px] poppinsMedium text-my_profile_secondary_text">
              {{ fmt(stage?.effectiveWeight) }}%
            </td>
            <td class="py-[18px] px-4 text-center text-[15px] poppinsMedium text-my_profile_secondary_text">
              {{ fmt(stage?.achievedScore) }} / {{ fmt(stage?.totalScore) }}
            </td>
            <td class="py-[18px] px-4">
              <span class="flex items-center gap-2 text-[15px] poppinsMedium text-my_profile_secondary_text">
                <span
                  class="w-2 h-2 rounded-full flex-shrink-0"
                  [ngClass]="{
                    'bg-Active_Otp_Border': proctoringTone(stage?.proctoringResult) === 'green',
                    'bg-status_amber': proctoringTone(stage?.proctoringResult) === 'amber',
                    'bg-status_red': proctoringTone(stage?.proctoringResult) === 'red',
                    'bg-slate_400': proctoringTone(stage?.proctoringResult) === 'grey'
                  }"
                ></span>
                {{ proctoringLabel(stage?.proctoringResult) }}
              </span>
            </td>
            <td class="py-[18px] px-4 text-center text-[15px] poppinsMedium text-my_profile_secondary_text">
              {{ fmt(stage?.score) }}%
            </td>
            <td class="py-[18px] px-4 text-center text-[15px] poppinsSemiBold text-dark_navy">
              {{ fmt(stage?.weightedContribution) }}
            </td>
          </tr>
          <tr *ngIf="finalSummary?.evaluationScores?.length" class="bg-slate_50 border-t border-blue_Color">
            <td class="py-[18px] px-4 text-[15px] poppinsSemiBold text-blue_Color">Total</td>
            <td class="py-[18px] px-4 text-center text-[15px] poppinsSemiBold text-blue_Color">
              {{ fmt(finalSummary?.effectiveWeightTotal) }}%
            </td>
            <td class="py-[18px] px-4 text-center text-[15px] poppinsSemiBold text-blue_Color">—</td>
            <td class="py-[18px] px-4 text-[15px] poppinsSemiBold text-blue_Color">—</td>
            <td class="py-[18px] px-4 text-center text-[15px] poppinsSemiBold text-blue_Color">
              {{ fmt(finalSummary?.finalScore) }}%
            </td>
            <td class="py-[18px] px-4 text-center text-[15px] poppinsSemiBold text-blue_Color">
              {{ fmt(finalSummary?.weightedScoreTotal) }}
            </td>
          </tr>
          <tr *ngIf="!finalSummary?.evaluationScores?.length">
            <td colspan="6" class="py-8 px-4 text-center text-sm text-slate_500 poppinsMedium">
              No stage scores are available yet.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <p
      *ngIf="finalSummary?.evaluationScores?.length"
      class="py-3 px-5 bg-light_blue border-t border-slate_200 text-[13px] leading-5 text-slate_600 poppinsMedium"
    >
      Weighted score is calculated as
      <span class="poppinsSemiBold text-blue_Color">Achieved × Weight</span>.
    </p>
  </div>
  </ng-container>
  <ng-container
    *ngIf="isSectionMissing('finalSummary')"
    [ngTemplateOutlet]="sectionUnavailable"
    [ngTemplateOutletContext]="{ $implicit: 'Stage Scores' }"
  ></ng-container>

  <!-- Resume stage (resumeMatching) -->
  <div
    *ngIf="driveAssessmentData?.resumeMatching as match"
    class="bg-white border border-slate_200 rounded-[10px] shadow-card-section overflow-hidden"
  >
    <div class="flex flex-wrap items-center justify-between gap-2 py-3.5 px-4 border-b border-slate_200">
      <div class="flex items-center gap-2">
        <span class="w-1 h-6 rounded-[1px_2px_2px_1px] bg-health-yellow-gradient"></span>
        <h2 class="text-base leading-[140%] text-slate_700 poppinsSemiBold">Resume Match</h2>
      </div>
      <span *ngIf="match?.status" class="status-pill" [ngClass]="resumeStatusTone(match?.status)">
        {{ match?.status | replaceUnderscore }}
      </span>
    </div>

    <div class="grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate_200 border-b border-slate_200">
      <div class="bg-white py-4 px-5">
        <p class="text-xs poppinsMedium text-slate_500">Resume score</p>
        <p class="mt-1 text-[22px] leading-[120%] poppinsBold text-dark_blue tabular-nums">
          {{ fmt(match?.resumeScore) }}
          <span class="text-sm poppinsMedium text-slate_400">/ cut-off {{ fmt(match?.resumeCutoff) }}</span>
        </p>
      </div>
      <div class="bg-white py-4 px-5">
        <p class="text-xs poppinsMedium text-slate_500">Experience</p>
        <p class="mt-1 text-[15px] poppinsSemiBold text-dark_navy">
          {{ match?.candidateYoe || "-" }} yrs
          <span class="text-sm poppinsMedium text-slate_400">(required {{ match?.requiredYoe || "-" }})</span>
        </p>
      </div>
      <div class="bg-white py-4 px-5">
        <p class="text-xs poppinsMedium text-slate_500">Tenure (overall / longest / recent)</p>
        <p class="mt-1 text-[15px] poppinsSemiBold text-dark_navy tabular-nums">
          {{ fmt(match?.overallTenure) }} / {{ fmt(match?.longestTenure) }} / {{ fmt(match?.recentTenure) }} yrs
        </p>
      </div>
      <div class="bg-white py-4 px-5">
        <p class="text-xs poppinsMedium text-slate_500">Rehire</p>
        <p class="mt-1 text-[15px] poppinsSemiBold text-dark_navy">{{ match?.rehireFlag || "-" }}</p>
      </div>
    </div>

    <div class="flex flex-wrap gap-x-6 gap-y-1 py-3 px-5 border-b border-slate_200 bg-slate_50 text-sm poppinsMedium text-slate_500">
      <span>Skills <span class="poppinsSemiBold text-dark_navy">{{ fmt(match?.skillScore) }}</span></span>
      <span>Experience <span class="poppinsSemiBold text-dark_navy">{{ fmt(match?.yoeScore) }}</span></span>
      <span>Tenure <span class="poppinsSemiBold text-dark_navy">{{ fmt(match?.tenureScore) }}</span></span>
    </div>

    <div class="overflow-x-auto">
      <table class="report-table w-full border-collapse min-w-[40rem]">
        <thead class="bg-light_blue uppercase poppinsSemiBold text-sm text-dark_blue tracking-[0.025em]">
          <tr>
            <th scope="col" class="p-4 text-left border-b border-slate_300 whitespace-nowrap w-40">Skills</th>
            <th scope="col" class="p-4 text-center border-b border-slate_300 whitespace-nowrap w-24">Score</th>
            <th scope="col" class="p-4 text-left border-b border-slate_300 whitespace-nowrap">Matched</th>
            <th scope="col" class="p-4 text-left border-b border-slate_300 whitespace-nowrap">Missing</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let group of resumeSkillGroups" class="border-b border-grey_150 last:border-b-0 align-top">
            <td class="py-4 px-4 text-[15px] poppinsMedium text-dark_navy">{{ group.label }}</td>
            <td class="py-4 px-4 text-[15px] text-center poppinsSemiBold text-dark_navy">{{ fmt(group.score) }}</td>
            <td class="py-4 px-4">
              <div class="flex flex-wrap gap-1.5">
                <span
                  *ngFor="let skill of group.matched"
                  class="rounded-md bg-green_100 text-green_600 text-xs poppinsMedium py-[3px] px-2"
                >{{ skill }}</span>
                <span *ngIf="!group.matched.length" class="is-empty text-sm">-</span>
              </div>
            </td>
            <td class="py-4 px-4">
              <div class="flex flex-wrap gap-1.5">
                <span
                  *ngFor="let skill of group.missing"
                  class="rounded-md bg-red_50 text-red_600 text-xs poppinsMedium py-[3px] px-2"
                >{{ skill }}</span>
                <span *ngIf="!group.missing.length" class="is-empty text-sm">-</span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <p
      *ngIf="match?.missingCriticalSkills?.length"
      class="py-3 px-5 border-t border-slate_200 bg-red_50 text-sm poppinsMedium text-red_600"
    >
      Missing critical skills: {{ match.missingCriticalSkills.join(", ") }}
    </p>
  </div>
  <ng-container
    *ngIf="isSectionMissing('resumeMatching')"
    [ngTemplateOutlet]="sectionUnavailable"
    [ngTemplateOutletContext]="{ $implicit: 'Resume Match' }"
  ></ng-container>

  <!-- Assessment stage -->
  <ng-container *ngIf="finalAssessment as asmt">
    <div class="grid grid-cols-1 xl:grid-cols-2 gap-5">
      <div class="bg-white border border-slate_200 rounded-[10px] shadow-card-section overflow-hidden">
        <div class="flex flex-wrap items-center justify-between gap-2 py-3.5 px-4 border-b border-slate_200">
          <div class="flex items-center gap-2">
            <span class="w-1 h-6 rounded-[1px_2px_2px_1px] bg-health-yellow-gradient"></span>
            <h2 class="text-base leading-[140%] text-slate_700 poppinsSemiBold">Assessment Details</h2>
          </div>
        </div>
        <div class="info-row">
          <span class="info-row__icon bg-violet_100">
            <img [src]="_commonServices.Images.CANDIDATE_INFO_ID_CARD" alt="" />
          </span>
          <span class="info-row__label text-dark_blue poppinsSemiBold">Drive ID</span>
          <span class="info-row__value text-my_profile_secondary_text poppinsMedium">{{ asmt?.driveId || "-" }}</span>
        </div>
        <div class="info-row">
          <span class="info-row__icon bg-violet_100">
            <img [src]="_commonServices.Images.CANDIDATE_INFO_CLOCK" alt="" />
          </span>
          <span class="info-row__label text-dark_blue poppinsSemiBold">Allowed duration</span>
          <span class="info-row__value text-my_profile_secondary_text poppinsMedium">{{ asmt?.durationMinutes ?? "-" }} Mins</span>
        </div>
        <div class="info-row">
          <span class="info-row__icon bg-violet_100">
            <img [src]="_commonServices.Images.SCORE_EVALUATION_SECTION" alt="" />
          </span>
          <span class="info-row__label text-dark_blue poppinsSemiBold">Questions</span>
          <span class="info-row__value text-my_profile_secondary_text poppinsMedium">{{ asmt?.totalQuestions ?? "-" }}</span>
        </div>
        <div class="info-row">
          <span class="info-row__icon bg-violet_100">
            <img [src]="_commonServices.Images.CANDIDATE_INFO_CLOCK" alt="" />
          </span>
          <span class="info-row__label text-dark_blue poppinsSemiBold">Started</span>
          <span class="info-row__value text-my_profile_secondary_text poppinsMedium">{{ asmt?.startTime || "-" }}</span>
        </div>
        <div class="info-row">
          <span class="info-row__icon bg-violet_100">
            <img [src]="_commonServices.Images.CANDIDATE_INFO_CLOCK" alt="" />
          </span>
          <span class="info-row__label text-dark_blue poppinsSemiBold">Finished</span>
          <span class="info-row__value text-my_profile_secondary_text poppinsMedium">{{ asmt?.endTime || "-" }}</span>
        </div>
      </div>
      <div class="bg-white border border-slate_200 rounded-[10px] shadow-card-section overflow-hidden">
        <div class="flex flex-wrap items-center justify-between gap-2 py-3.5 px-4 border-b border-slate_200">
          <div class="flex items-center gap-2">
            <span class="w-1 h-6 rounded-[1px_2px_2px_1px] bg-health-yellow-gradient"></span>
            <h2 class="text-base leading-[140%] text-slate_700 poppinsSemiBold">Proctoring</h2>
          </div>
        </div>
        <div class="info-row">
          <span class="info-row__icon bg-blue_50">
            <img [src]="_commonServices.Images.SCORE_EVALUATION_SECTION" alt="" />
          </span>
          <span class="info-row__label text-dark_blue poppinsSemiBold">System verdict</span>
          <span class="info-row__value flex items-center gap-2 text-my_profile_secondary_text poppinsMedium">
            <span
              class="w-2 h-2 rounded-full flex-shrink-0"
              [ngClass]="{
                'bg-Active_Otp_Border': proctoringTone(asmt?.systemProctoringVerdict) === 'green',
                'bg-status_amber': proctoringTone(asmt?.systemProctoringVerdict) === 'amber',
                'bg-status_red': proctoringTone(asmt?.systemProctoringVerdict) === 'red',
                'bg-slate_400': proctoringTone(asmt?.systemProctoringVerdict) === 'grey'
              }"
            ></span>
            {{ proctoringLabel(asmt?.systemProctoringVerdict) }}
          </span>
        </div>
        <div class="info-row">
          <span class="info-row__icon" [ngClass]="(asmt?.tabSwitches > TAB_SWITCH_LIMIT) ? 'bg-red_50' : 'bg-green_100'">
            <img
              [src]="(asmt?.tabSwitches > TAB_SWITCH_LIMIT) ? _commonServices.Images.STATUS_WARNING_TRIANGLE : _commonServices.Images.STATUS_CHECK_CIRCLE"
              alt=""
            />
          </span>
          <span class="info-row__label text-dark_blue poppinsSemiBold">Tab switches</span>
          <span
            class="info-row__value poppinsMedium"
            [ngClass]="(asmt?.tabSwitches > TAB_SWITCH_LIMIT) ? 'text-red_600' : 'text-green_600'"
          >{{ asmt?.tabSwitches ?? 0 }}</span>
        </div>
        <div class="info-row">
          <span class="info-row__icon" [ngClass]="(asmt?.backgroundApp && asmt?.backgroundApp !== 'Not Detected') ? 'bg-red_50' : 'bg-green_100'">
            <img
              [src]="(asmt?.backgroundApp && asmt?.backgroundApp !== 'Not Detected') ? _commonServices.Images.STATUS_WARNING_TRIANGLE : _commonServices.Images.STATUS_CHECK_CIRCLE"
              alt=""
            />
          </span>
          <span class="info-row__label text-dark_blue poppinsSemiBold">Background apps</span>
          <span
            class="info-row__value poppinsMedium"
            [ngClass]="(asmt?.backgroundApp && asmt?.backgroundApp !== 'Not Detected') ? 'text-red_600' : 'text-green_600'"
          >{{ asmt?.backgroundApp || "-" }}</span>
        </div>
        <div class="info-row">
          <span class="info-row__icon" [ngClass]="(asmt?.externalHardware && asmt?.externalHardware !== 'Not Used') ? 'bg-red_50' : 'bg-green_100'">
            <img
              [src]="(asmt?.externalHardware && asmt?.externalHardware !== 'Not Used') ? _commonServices.Images.STATUS_WARNING_TRIANGLE : _commonServices.Images.STATUS_CHECK_CIRCLE"
              alt=""
            />
          </span>
          <span class="info-row__label text-dark_blue poppinsSemiBold">External hardware</span>
          <span
            class="info-row__value poppinsMedium"
            [ngClass]="(asmt?.externalHardware && asmt?.externalHardware !== 'Not Used') ? 'text-red_600' : 'text-green_600'"
          >{{ asmt?.externalHardware || "-" }}</span>
        </div>
      </div>
    </div>

    <div class="bg-white border border-slate_200 rounded-[10px] shadow-card-section overflow-hidden">
        <div class="flex flex-wrap items-center justify-between gap-2 py-3.5 px-4 border-b border-slate_200">
          <div class="flex items-center gap-2">
            <span class="w-1 h-6 rounded-[1px_2px_2px_1px] bg-health-yellow-gradient"></span>
            <h2 class="text-base leading-[140%] text-slate_700 poppinsSemiBold">Assessment Sections</h2>
          </div>
        </div>
      <div class="overflow-x-auto">
        <table class="report-table w-full border-collapse min-w-[52rem]">
          <thead class="bg-light_blue uppercase poppinsSemiBold text-sm text-dark_blue tracking-[0.025em]">
            <tr>
              <th scope="col" class="p-4 text-left border-b border-slate_300 whitespace-nowrap w-16">S.No</th>
              <th scope="col" class="p-4 text-left border-b border-slate_300 whitespace-nowrap">Section</th>
              <th scope="col" class="p-4 text-center border-b border-slate_300 whitespace-nowrap">Score</th>
              <th scope="col" class="p-4 text-center border-b border-slate_300 whitespace-nowrap">Questions</th>
              <th scope="col" class="p-4 text-center border-b border-slate_300 whitespace-nowrap">Correct</th>
              <th scope="col" class="p-4 text-center border-b border-slate_300 whitespace-nowrap">Incorrect</th>
              <th scope="col" class="p-4 text-center border-b border-slate_300 whitespace-nowrap">Unattempted</th>
              <th scope="col" class="p-4 text-left border-b border-slate_300 whitespace-nowrap">Status</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let sec of asmt?.sections; index as i" class="border-b border-grey_150 last:border-b-0">
              <td class="py-[18px] px-4 text-[15px] poppinsMedium text-slate_500">{{ sec?.sno ?? i + 1 }}</td>
              <td class="py-[18px] px-4 text-[15px] leading-[140%] poppinsMedium text-dark_navy">{{ sec?.section || "-" }}</td>
              <td class="py-[18px] px-4 text-[15px] text-center poppinsSemiBold text-dark_navy">{{ fmt(sec?.score) }}</td>
              <td class="py-[18px] px-4 text-[15px] text-center poppinsMedium text-my_profile_secondary_text">{{ sec?.numberOfQuestions ?? "-" }}</td>
              <td class="py-[18px] px-4 text-[15px] text-center poppinsMedium text-my_profile_secondary_text">{{ sec?.correct ?? "-" }}</td>
              <td class="py-[18px] px-4 text-[15px] text-center poppinsMedium text-my_profile_secondary_text">{{ sec?.incorrect ?? "-" }}</td>
              <td class="py-[18px] px-4 text-[15px] text-center poppinsMedium text-my_profile_secondary_text">{{ sec?.unattempted ?? "-" }}</td>
              <td class="py-[18px] px-4">
                <span *ngIf="sec?.status; else emptyCell" class="status-pill" [ngClass]="sectionStatusTone(sec?.status)">
                  {{ sec?.status | replaceUnderscore }}
                </span>
              </td>
            </tr>
            <tr *ngIf="!asmt?.sections?.length">
              <td colspan="8" class="py-8 px-4 text-center text-sm text-slate_500 poppinsMedium">No sections recorded.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Question-by-question responses -->
    <div *ngIf="asmt?.questions?.length" class="bg-white border border-slate_200 rounded-[10px] shadow-card-section overflow-hidden">
        <div class="flex flex-wrap items-center justify-between gap-2 py-3.5 px-4 border-b border-slate_200">
          <div class="flex items-center gap-2">
            <span class="w-1 h-6 rounded-[1px_2px_2px_1px] bg-health-yellow-gradient"></span>
            <h2 class="text-base leading-[140%] text-slate_700 poppinsSemiBold">Question Responses</h2>
          </div>
          <span class="text-sm text-slate_500 poppinsMedium">{{ asmt?.questions?.length }} questions</span>
        </div>
      <ol class="max-h-[40rem] overflow-y-auto changeScrollbarStyle">
        <li
          *ngFor="let q of asmt?.questions; index as i"
          class="grid grid-cols-[2.5rem_minmax(0,1fr)] gap-x-3 py-4 px-5 border-b border-grey_150 last:border-b-0"
        >
          <span class="text-sm poppinsSemiBold text-slate_400 tabular-nums">Q{{ i + 1 }}</span>
          <div class="flex flex-col gap-2 min-w-0">
            <div class="flex flex-wrap items-center gap-2 text-xs poppinsMedium text-slate_500">
              <span class="rounded-md bg-slate_50 border border-slate_200 py-0.5 px-2">{{ q?.category || "Uncategorised" }}</span>
              <span *ngIf="q?.difficultyLevel != null">Difficulty {{ q?.difficultyLevel }}</span>
              <span *ngIf="q?.timeSpentSeconds != null">{{ q?.timeSpentSeconds }}s spent</span>
              <span
                class="ml-auto poppinsSemiBold text-sm tabular-nums"
                [ngClass]="q?.marksScored > 0 ? 'text-green_600' : q?.marksScored < 0 ? 'text-red_600' : 'text-slate_500'"
              >
                {{ fmt(q?.marksScored) }} / {{ fmt(q?.maxMarks) }}
              </span>
            </div>
            <div class="question-text text-sm leading-[22px] poppinsMedium text-dark_navy" [innerHTML]="q?.questionText"></div>
            <div
              *ngIf="q?.selectedAnswer != null || q?.correctAnswer != null"
              class="grid grid-cols-1 md:grid-cols-2 gap-2"
            >
              <div
                class="rounded-lg border py-2 px-3"
                [ngClass]="isCorrectAnswer(q) ? 'bg-green_100 border-green_100' : 'bg-red_50 border-red_50'"
              >
                <p class="text-xs poppinsSemiBold" [ngClass]="isCorrectAnswer(q) ? 'text-green_600' : 'text-red_600'">
                  Candidate's answer
                </p>
                <div class="question-text text-sm leading-[21px] poppinsMedium text-dark_navy" [innerHTML]="q?.selectedAnswer || 'Not answered'"></div>
              </div>
              <div *ngIf="!isCorrectAnswer(q) && q?.correctAnswer != null" class="rounded-lg border border-slate_200 bg-slate_50 py-2 px-3">
                <p class="text-xs poppinsSemiBold text-slate_500">Correct answer</p>
                <div class="question-text text-sm leading-[21px] poppinsMedium text-dark_navy" [innerHTML]="q?.correctAnswer || '-'"></div>
              </div>
            </div>
          </div>
        </li>
      </ol>
    </div>
  </ng-container>
  <ng-container
    *ngIf="isSectionMissing('assessment')"
    [ngTemplateOutlet]="sectionUnavailable"
    [ngTemplateOutletContext]="{ $implicit: 'Assessment Details' }"
  ></ng-container>

  <!-- Interview-bot stage -->
  <ng-container *ngIf="finalBot as bot">
    <div class="bg-white border border-slate_200 rounded-[10px] shadow-card-section overflow-hidden">
        <div class="flex flex-wrap items-center justify-between gap-2 py-3.5 px-4 border-b border-slate_200">
          <div class="flex items-center gap-2">
            <span class="w-1 h-6 rounded-[1px_2px_2px_1px] bg-health-yellow-gradient"></span>
            <h2 class="text-base leading-[140%] text-slate_700 poppinsSemiBold">Interview Transcript</h2>
          </div>
          <span class="text-sm text-slate_500 poppinsMedium">
            Score <span class="text-dark_navy poppinsSemiBold">{{ fmt(bot?.score) }}</span>
            <ng-container *ngIf="bot?.focusOut != null">, focus lost {{ bot?.focusOut }} times</ng-container>
          </span>
        </div>
      <ng-container
        *ngTemplateOutlet="transcriptBody; context: { $implicit: bot?.conversation }"
      ></ng-container>
    </div>

    <div *ngIf="bot?.feedback?.length" class="bg-white border border-slate_200 rounded-[10px] shadow-card-section overflow-hidden">
        <div class="flex flex-wrap items-center justify-between gap-2 py-3.5 px-4 border-b border-slate_200">
          <div class="flex items-center gap-2">
            <span class="w-1 h-6 rounded-[1px_2px_2px_1px] bg-health-yellow-gradient"></span>
            <h2 class="text-base leading-[140%] text-slate_700 poppinsSemiBold">Topic Feedback</h2>
          </div>
        </div>
      <details *ngFor="let topic of bot?.feedback; first as isFirst" class="topic-feedback border-b border-grey_150 last:border-b-0" [open]="isFirst">
        <summary class="flex items-center justify-between gap-3 py-4 px-5 cursor-pointer text-[15px] poppinsSemiBold text-dark_navy first-letter:uppercase">
          {{ topic?.topicName || "-" }}
          <span class="topic-feedback__chevron text-slate_400" aria-hidden="true">+</span>
        </summary>
        <ul class="flex flex-col gap-2 pb-4 px-5 list-disc pl-10">
          <li *ngFor="let point of topic?.points" class="text-sm leading-[22px] poppinsMedium text-my_profile_secondary_text">
            {{ point }}
          </li>
        </ul>
      </details>
    </div>
  </ng-container>
  <ng-container
    *ngIf="isSectionMissing('interviewBot')"
    [ngTemplateOutlet]="sectionUnavailable"
    [ngTemplateOutletContext]="{ $implicit: 'Interview Transcript' }"
  ></ng-container>
</ng-template>

<!-- SR section the report type covers but the backend has no data for yet -->
<ng-template #sectionUnavailable let-title>
  <div class="bg-white border border-slate_200 rounded-[10px] shadow-card-section overflow-hidden">
    <div class="flex items-center gap-2 py-3.5 px-4 border-b border-slate_200">
      <span class="w-1 h-6 rounded-[1px_2px_2px_1px] bg-health-yellow-gradient"></span>
      <h2 class="text-base leading-[140%] text-slate_700 poppinsSemiBold">{{ title }}</h2>
    </div>
    <p class="py-8 px-4 text-center text-sm text-slate_500 poppinsMedium">Not available</p>
  </div>
</ng-template>
</div>
